// src/pages/Messages.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Send, Settings, ArrowLeft, MoreVertical, X, MessageCircle, Mail, PenSquare } from 'lucide-react';
import { auth } from '../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  subscribeToConversations,
  subscribeToMessages,
  sendMessage,
  markAsRead,
  archiveConversation,
  unarchiveConversation,
} from '../services/messaging-service';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getInitials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const formatTimestamp = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatTime = (date) => {
  if (!date) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------
const Messages = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-select conversation from ?conv= param (e.g. after ComposeMessage redirect)
  const convParam = searchParams.get('conv');

  const [practitionerId, setPractitionerId] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [convLoading, setConvLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'unread' | 'archived'
  const [showMobileThread, setShowMobileThread] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Auth — onAuthStateChanged + done flag + 3s timeout (safe pattern)
  useEffect(() => {
    if (!auth) { setAuthLoading(false); return; }
    let done = false;
    const timeout = setTimeout(() => { if (!done) { done = true; setAuthLoading(false); } }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        if (user) setPractitionerId(user.uid);
        setAuthLoading(false);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Subscribe to conversations once we have practitionerId
  useEffect(() => {
    if (!practitionerId) return;
    setConvLoading(true);
    const unsub = subscribeToConversations(practitionerId, (convs) => {
      setConversations(convs);
      setConvLoading(false);
    });
    return () => unsub();
  }, [practitionerId]);

  // Auto-select conversation: if ?conv= param present, always prefer it once loaded.
  // This ensures a newly created conversation is selected even if conversations
  // loaded before the new one arrived (Fix 2 — email mismatch race condition).
  useEffect(() => {
    if (conversations.length === 0) return;
    if (convParam) {
      const found = conversations.find(c => c.id === convParam);
      if (found) {
        setSelectedConvId(convParam);
        setShowMobileThread(true);
      }
    } else if (!selectedConvId) {
      const first = conversations.find(c => c.status === 'active') || conversations[0];
      setSelectedConvId(first.id);
    }
  }, [conversations, convParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to messages when a conversation is selected
  useEffect(() => {
    if (!practitionerId || !selectedConvId) { setMessages([]); return; }
    setMsgLoading(true);
    const unsub = subscribeToMessages(practitionerId, selectedConvId, (msgs) => {
      setMessages(msgs);
      setMsgLoading(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    });
    return () => unsub();
  }, [practitionerId, selectedConvId]);

  // Mark as read when opening a conversation
  useEffect(() => {
    if (practitionerId && selectedConvId) {
      markAsRead(practitionerId, selectedConvId);
    }
  }, [practitionerId, selectedConvId]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    if (showMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  // Handle send
  const handleSendMessage = async () => {
    const text = newMessage.trim();
    if (!text || !selectedConvId || !practitionerId || sending) return;
    setSending(true);
    setSendError('');
    setNewMessage('');
    try {
      await sendMessage(practitionerId, selectedConvId, text);
    } catch (err) {
      console.error('Messages: send failed:', err);
      setSendError('Failed to send. Please try again.');
      setNewMessage(text);
    } finally {
      setSending(false);
    }
  };

  const handleSelectConversation = (id) => {
    setSelectedConvId(id);
    setShowMobileThread(true);
    setSendError('');
  };

  const handleArchive = async () => {
    if (!practitionerId || !selectedConvId) return;
    setShowMenu(false);
    await archiveConversation(practitionerId, selectedConvId);
    setSelectedConvId(null);
    setShowMobileThread(false);
  };

  const handleUnarchive = async () => {
    if (!practitionerId || !selectedConvId) return;
    setShowMenu(false);
    await unarchiveConversation(practitionerId, selectedConvId);
    setActiveTab('all');
    setSelectedConvId(null);
    setShowMobileThread(false);
  };

  // Filtered list
  const filteredConversations = conversations.filter((conv) => {
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      conv.clientName.toLowerCase().includes(q) ||
      conv.subject.toLowerCase().includes(q) ||
      conv.lastMessagePreview.toLowerCase().includes(q);
    if (activeTab === 'archived') return matchSearch && conv.status === 'archived';
    if (activeTab === 'unread') return matchSearch && conv.status !== 'archived' && conv.unreadCount > 0;
    return matchSearch && conv.status !== 'archived';
  });

  const currentConv = conversations.find((c) => c.id === selectedConvId);

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (!practitionerId) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <p className="text-gray-500 text-sm">Please sign in to view messages.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-sm text-gray-600 mt-1">
              {conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0)} unread
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/messages/compose')}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
            >
              <PenSquare className="w-4 h-4" />
              New Message
            </button>
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/settings/notifications')}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/dashboard/settings/notifications'); }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors cursor-pointer"
            >
              <Settings className="w-4 h-4" />
              Settings
            </span>
          </div>
        </div>

        {/* Main container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 240px)' }}>
          <div className="flex h-full">
            {/* ── Left sidebar ── */}
            <div className={`${showMobileThread ? 'hidden md:flex' : 'flex'} w-full md:w-[340px] border-r border-gray-200 flex-col`}>
              {/* Search + filter */}
              <div className="p-4 border-b border-gray-200 space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-100 rounded-full"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'unread', label: 'Unread' },
                    { id: 'archived', label: 'Archived' },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => { setActiveTab(id); setSelectedConvId(null); }}
                      className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conversation list */}
              <div className="flex-1 overflow-y-auto">
                {convLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <MessageCircle className="w-14 h-14 text-gray-300 mb-4" />
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {searchQuery ? 'No results' : 'No conversations yet'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {searchQuery ? 'Try a different search term' : 'Start a conversation with a client to get going'}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => navigate('/messages/compose')}
                        className="mt-4 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        New Message
                      </button>
                    )}
                  </div>
                ) : (
                  filteredConversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => handleSelectConversation(conv.id)}
                      className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                        selectedConvId === conv.id
                          ? 'bg-purple-50 border-l-4 border-l-purple-600'
                          : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-semibold flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)', backgroundSize: '400% 400%', animation: 'auroraShift 16s ease infinite' }}
                        >
                          {getInitials(conv.clientName)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <h3 className={`text-sm truncate ${conv.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
                              {conv.clientName}
                            </h3>
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                              {formatTimestamp(conv.lastMessageAt)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate mb-1 font-medium">{conv.subject}</p>
                          <div className="flex items-center justify-between">
                            <p className={`text-xs truncate ${conv.unreadCount > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                              {conv.lastMessageBy === 'practitioner' ? 'You: ' : ''}{conv.lastMessagePreview}
                            </p>
                            {conv.unreadCount > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 bg-purple-600 text-white text-xs rounded-full flex-shrink-0 min-w-[20px] text-center font-bold">
                                {conv.unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── Right thread panel ── */}
            <div className={`${showMobileThread ? 'flex' : 'hidden md:flex'} flex-1 flex-col`}>
              {currentConv ? (
                <>
                  {/* Thread header */}
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowMobileThread(false)}
                        className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                      </button>
                      <div
                        className="w-10 h-10 rounded-full text-white flex items-center justify-center text-sm font-semibold"
                        style={{ background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #bb9749, #4c1d95, #22d3ee, #6366f1, #bb9749, #7c3aed)', backgroundSize: '400% 400%', animation: 'auroraShift 16s ease infinite' }}
                      >
                        {getInitials(currentConv.clientName)}
                      </div>
                      <div>
                        <h2 className="text-sm font-semibold text-gray-900">{currentConv.clientName}</h2>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {currentConv.clientEmail}
                        </p>
                      </div>
                    </div>
                    <div className="relative" ref={menuRef}>
                      <button
                        onClick={() => setShowMenu((p) => !p)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-5 h-5 text-gray-600" />
                      </button>
                      {showMenu && (
                        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          {currentConv?.status === 'archived' ? (
                            <button
                              onClick={handleUnarchive}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              Unarchive
                            </button>
                          ) : (
                            <button
                              onClick={handleArchive}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              Archive conversation
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Subject banner */}
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-700">Subject: </span>
                      {currentConv.subject}
                    </p>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {msgLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="text-center py-8 text-sm text-gray-400">No messages yet</div>
                    ) : (
                      messages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.sender === 'practitioner' ? 'justify-end' : 'justify-start'}`}>
                          <div className="max-w-[72%]">
                            <div
                              className="rounded-2xl px-4 py-2.5 shadow-sm"
                              style={msg.sender === 'practitioner'
                                ? { background: '#1F2937', color: '#fff' }
                                : { background: '#F3F4F6', color: '#374151' }
                              }
                            >
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <div className={`flex items-center gap-1 mt-1 ${msg.sender === 'practitioner' ? 'justify-end' : 'justify-start'}`}>
                              <p className="text-xs text-gray-400">{formatTime(msg.sentAt)}</p>
                              {msg.viaEmail && (
                                <span className="flex items-center gap-0.5 text-xs text-gray-400">
                                  <Mail className="w-3 h-3" />
                                  via email
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Send error */}
                  {sendError && (
                    <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                      <p className="text-sm text-red-600">{sendError}</p>
                      <button onClick={() => setSendError('')}><X className="w-4 h-4 text-red-400" /></button>
                    </div>
                  )}

                  {/* Message input */}
                  <div className="p-4 border-t border-gray-200">
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          placeholder="Type a message... (this will be emailed to your client)"
                          className="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                          rows={2}
                          style={{ minHeight: '44px', maxHeight: '120px' }}
                        />
                      </div>
                      <button
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || sending}
                        className="p-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending
                          ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          : <Send className="w-5 h-5" />
                        }
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      Your message will be delivered by email. The client can reply directly.
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <MessageCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Select a conversation</h3>
                    <p className="text-sm text-gray-500 mb-4">Choose from the list or start a new message</p>
                    <button
                      onClick={() => navigate('/messages/compose')}
                      className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      New Message
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
